/**
 * Mail-Vorlagen — Platzhalter-Engine + BKK-Pflegefinder-Link-Builder.
 *
 * Vorlagen aus mail_vorlagen.body_template enthalten Platzhalter wie
 * {{vorname}}, {{plz}}, {{bkk_pflegefinder_link}}. Vor dem Einfuegen in
 * den Mail-Body werden sie aus dem aktuellen Kunden-Datensatz ersetzt.
 *
 * Unbekannte Platzhalter bleiben unveraendert — so sieht der Berater
 * im Editor, was er ggf. noch von Hand ergaenzen muss.
 */

/**
 * Ersetzt {{key}}-Platzhalter in template aus dem kontext-Objekt.
 * Leere / fehlende Werte bleiben als '{{key}}' im Text stehen.
 */
export function platzhalterErsetzen(template, kontext) {
  if (!template) return '';
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const wert = kontext?.[key];
    if (wert === undefined || wert === null || wert === '') return match;
    return String(wert);
  });
}

/**
 * Baut die Such-URL fuer den BKK-Pflegefinder mit vorgefuellten Parametern.
 *
 * Hinweis zur 'location'-ID: Die BKK-Seite mappt PLZ -> numerische Stadtteil-
 * ID intern. Wenn wir nur die PLZ uebergeben (ohne location), zeigt die
 * Seite eine Stadtteil-Auswahl-Maske — der Empfaenger klickt einmal mehr,
 * sieht dann seine Anbieter. Das ist sauber + zukunftssicher.
 *
 * service-Werte (aus Web-UI rausgelesen):
 *   - 'everydaySupport' = Angebote zur Unterstuetzung im Alltag
 *                          (§ 45a SGB XI Entlastungsleistungen)
 *   Weitere koennen wir spaeter hinzufuegen, sobald wir sie aus der
 *   Web-UI rausschauen.
 */
export function bauePflegefinderLink(plz, options = {}) {
  const umkreis = options.umkreis ?? 5;
  const service = options.service || 'everydaySupport';
  const params = new URLSearchParams();
  params.append('searchdata[order]', 'distance');
  if (plz) params.append('searchdata[zip]', String(plz));
  params.append('searchdata[maxDistance]', String(umkreis));
  params.append('redirectToSpecialSearch', '1');
  params.append('searchdata[service][]', service);
  return `https://pflegefinder.bkk-dachverband.de/suche/searchresult.php?${params.toString()}`;
}

/**
 * Aus dem Kunden-Datensatz das Kontext-Objekt fuer Vorlagen-Platzhalter
 * bauen. Aufgerufen, wenn der Berater eine Vorlage einfuegt.
 *
 * Feld-Namen entsprechen denen, die das Backend in /api/kunden liefert
 * (firstName / lastName / zip / city / salutation / careLevel / relative).
 */
export function baueVorlagenKontext(kunde) {
  if (!kunde) return {};
  const angehoerig = kunde.relative || {};
  return {
    anrede: kunde.salutation || '',
    vorname: kunde.firstName || '',
    nachname: kunde.lastName || '',
    name: `${kunde.firstName || ''} ${kunde.lastName || ''}`.trim(),
    plz: kunde.zip || '',
    ort: kunde.city || '',
    strasse: kunde.street || '',
    pflegegrad: kunde.careLevel ? `Pflegegrad ${kunde.careLevel}` : '',
    angehoeriger_anrede: angehoerig.salutation || '',
    angehoeriger_vorname: angehoerig.firstName || '',
    angehoeriger_nachname: angehoerig.lastName || '',
    angehoeriger_name: `${angehoerig.firstName || ''} ${angehoerig.lastName || ''}`.trim(),
    bkk_pflegefinder_link: bauePflegefinderLink(kunde.zip),
  };
}

// ==========================================================================
// Einzelfallentscheidung "dauerhaft Video" — modularer Textbaustein
// ==========================================================================
//
// Hendrik schickt Kunden, bei denen ein Antrag auf dauerhafte Video-
// Beratung sinnvoll ist, einen Erklaer-Text mit. Der Text hat einen
// festen Rahmen (Einleitung + alte Begleit-Argumente + Muster-Schreiben
// + Abschluss) und drei optional ankreuzbare medizinische Themen.
//
// Funktion baut den Text aus den angekreuzten Bausteinen zusammen.
// Reihenfolge der Themen-Absaetze ist fix (Long Covid -> Infektion ->
// Psyche), unabhaengig von der Klick-Reihenfolge im UI — damit die
// Mail strukturell immer gleich aussieht.
//
// Wortlaut von Hendrik am 2026-05-12 freigegeben.

const EINZELFALL_EINLEITUNG = `Einige meiner Kunden konnten ihre Pflegekasse davon überzeugen, die Beratungsbesuche dauerhaft als Videosprechstunde durchzuführen. Wichtig vorab: Es gibt darauf keinen Rechtsanspruch. Es handelt sich um eine Einzelfallentscheidung der Kasse — also Goodwill. Aus demselben Grund existiert auch kein formales Widerspruchsverfahren, wenn die Kasse ablehnt; man kann höchstens nochmal nachfragen oder mit zusätzlichen Belegen neu argumentieren.

In meiner Praxis haben sich folgende Argumente als Begleit-Begründungen bewährt — allein reichen sie häufig nicht, sie wirken erst zusammen mit einem individuellen medizinischen Grund:

- Es konnten trotz intensiver Suche keine ambulanten Dienste gefunden werden, die diese Beratung anbieten, oder bisherige Dienste haben aus Kapazitätsgründen abgesagt.
- Bei bereits hoher Zahl bisheriger Beratungseinsätze (vor allem bei Pflegegrad 4 und 5) sind durch einen weiteren Hausbesuch keine neuen Pflege-Erkenntnisse zu erwarten — er ist für die Versicherte und ihre pflegenden Angehörigen eher eine zusätzliche Belastung.
- Die Pflegeversicherung schreibt selbst vor: „Der Beratungsbesuch wird möglichst auf Dauer von derselben Beratungsperson durchgeführt." — eine wechselnde Beratungsperson widerspräche dieser Vorgabe.

Wir wissen, dass das ein echtes Dilemma für die Familien ist: diese Argumente alleine reichen oft nicht aus. Erst zusammen mit einer medizinisch begründeten Ausnahmesituation hat ein Antrag realistische Chancen. Hier kommen die individuell zutreffenden Themen:`;

const EINZELFALL_LONG_COVID = `Belastungsintoleranz nach Long Covid oder bei ME/CFS: Bei diesen Erkrankungen führt selbst eine kurze soziale Interaktion mit einer fremden Person im häuslichen Umfeld zu einer Post-Exertional Malaise (Crash) mit massiver Verschlechterung über Tage bis Wochen. Ein körperlicher Hausbesuch ist hier nicht nur unangenehm, sondern in vielen Fällen gesundheitsschädlich. Ein ärztliches Attest dazu (Hausarzt oder Spezialambulanz) ist sehr hilfreich.`;

const EINZELFALL_INFEKTION = `Hohes Infektionsrisiko, insbesondere im Atemwegsbereich: Bei schweren Atemwegserkrankungen, Immunsuppression oder einer Konstellation, in der ein Atemwegsinfekt für die zu pflegende Person akut lebensbedrohlich wäre, ist jeder zusätzliche Hausbesucher ein vermeidbares Risiko. Hier bewährt sich eine ärztliche Bescheinigung, die vom Besuch durch Dritte aus gesundheitlichen Gründen abrät.`;

const EINZELFALL_PSYCHE = `Psychische Belastungssituation: Bei Kindern oder Erwachsenen aus dem Autismus-Spektrum oder mit ADHS kann der Besuch einer fremden Person im häuslichen Schutzraum zu einer akuten Überforderung mit aggressivem oder regressivem Verhalten führen. Bei traumatisierten Personen — insbesondere mit Erfahrungen häuslicher Gewalt oder Übergriffen — ist es im Heilungsprozess kontraindiziert, fremde Menschen in die eigene Wohnung zu lassen. In all diesen Fällen ist die Videoberatung das deutlich weniger belastende Format. Eine fachärztliche oder psychotherapeutische Stellungnahme stärkt den Antrag erheblich.`;

const EINZELFALL_ABSCHLUSS = `Wichtig: Die obigen Formulierungen sind Muster. Bitte passen Sie sie unbedingt konkret und individuell auf die Situation Ihres Angehörigen an — eine 1:1-Übernahme erkennen die Kassen sofort und werten das eher negativ. Schildern Sie die tatsächliche Lebenssituation und legen Sie, wenn möglich, ein ärztliches Attest bei.

Stellen Sie den Antrag bitte schriftlich (E-Mail oder Brief), zum Beispiel in dieser Form:

„Hiermit bitte ich [Name der Pflegekasse] um die Genehmigung, die Beratungseinsätze nach § 37 Abs. 3 SGB XI dauerhaft als Videosprechstunde durchführen zu können. Diese Bitte um eine Einzelfallentscheidung begründe ich wie folgt: [hier eine oder mehrere der oben genannten Begründungen — individuell angepasst auf Ihre Situation — sowie ggf. weitere persönliche Gründe einfügen]. Über Ihre Entscheidung würde ich mich freuen. Mit freundlichen Grüßen, [Ihr Name]"

Sobald die Kasse Ihnen antwortet — egal ob positiv oder ablehnend — geben Sie mir bitte kurz Bescheid. Bei einer Genehmigung trage ich „dauerhaft Video" in Ihrer Akte ein, sodass ich für künftige Beratungen direkt das richtige Format einplane.`;

export const EINZELFALL_VIDEO_THEMEN = [
  { key: 'long_covid', label: 'Belastungsintoleranz (Long Covid / ME/CFS)', text: EINZELFALL_LONG_COVID },
  { key: 'infektion',  label: 'Infektionsrisiko (Atemwege)',                text: EINZELFALL_INFEKTION },
  { key: 'psyche',     label: 'Psychische Belastung (Autismus / ADHS / Trauma)', text: EINZELFALL_PSYCHE },
];

/**
 * Setzt den Einzelfall-Video-Text aus den ausgewaehlten Themen zusammen.
 * `auswahl` ist ein Objekt wie { long_covid: true, infektion: false, psyche: true }.
 * Liefert leeren String, wenn keine Themen angekreuzt sind — der UI-Button
 * sollte das vorher abfangen und disablen.
 */
export function baueEinzelfallVideoText(auswahl) {
  const gewaehlt = EINZELFALL_VIDEO_THEMEN.filter(t => auswahl?.[t.key]);
  if (gewaehlt.length === 0) return '';
  const teile = [EINZELFALL_EINLEITUNG, ...gewaehlt.map(t => t.text), EINZELFALL_ABSCHLUSS];
  return teile.join('\n\n');
}

// ==========================================================================
// No-Show-Rueckmeldung — empathischer Muster-Text (Wortlaut von Hendrik)
// ==========================================================================
//
// Wird verwendet, wenn ein Kunde nicht zum Termin erschienen ist. Bewusst
// als fester Baustein (keine KI), damit der Ton verlaesslich freundlich und
// nicht vorwurfsvoll ist. Personalisiert nur die Anrede. Enthaelt den
// datenschutzkonformen Hinweis auf Loeschung, falls kein Bedarf mehr besteht.

function _anredeZeile(kunde) {
  const s = (kunde?.salutation || '').trim();
  const nn = (kunde?.lastName || '').trim();
  if ((s === 'Frau' || s === 'Herr') && nn) return `Guten Tag ${s} ${nn},`;
  return 'Guten Tag,';
}

export function baueNoShowText(kunde) {
  return {
    betreff: 'Unser Termin heute — melden Sie sich gern',
    body: [
      _anredeZeile(kunde),
      '',
      'schade, dass unser vereinbarter Termin heute nicht stattfinden konnte. Ich hoffe, dass bei Ihnen alles in Ordnung ist.',
      '',
      'Falls weiterhin Beratungsbedarf besteht, melden Sie sich gerne, damit wir einen neuen Termin vereinbaren können. Sollte kein Bedarf mehr bestehen, genügt ebenfalls eine kurze Rückmeldung. Dann kann ich Ihre Daten aus Datenschutzgründen entsprechend löschen.',
      '',
      'Herzliche Grüße',
    ].join('\n'),
  };
}
